import React, { useState } from 'react';
import { Checkbox, Button, SparkIcon, TagColored, TooltipTrigger, SparkModalPortal, Modal as SparkModal, ReadOnlyGuard } from '@spark';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';

function RelationList({ relations, entities, onDelete, onBulkDelete, allCategories, onBulkValidate, onBulkUnvalidate }) {
    const [selectedRelations, setSelectedRelations] = useState(new Set());
    const [confirmBulkDeleteVisible, setConfirmBulkDeleteVisible] = useState(false);

    // Helper to get entity by start_index
    const getEntity = (start_index) => entities.find(e => e.start_index === start_index);

    // Handle individual relation selection
    const handleRelationSelect = (idx) => {
        const newSelected = new Set(selectedRelations);
        if (newSelected.has(idx)) {
            newSelected.delete(idx);
        } else {
            newSelected.add(idx);
        }
        setSelectedRelations(newSelected);
    };

    // Handle select all
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRelations(new Set(relations.map((_, idx) => idx)));
        } else {
            setSelectedRelations(new Set());
        }
    };

    const handleBulkDelete = () => {
        if (selectedRelations.size === 0) return;
        setConfirmBulkDeleteVisible(true);
    };

    const confirmBulkDelete = () => {
        if (onBulkDelete) {
            const selectedIndices = Array.from(selectedRelations);
            onBulkDelete(selectedIndices);
        } else {
            const sortedIndices = Array.from(selectedRelations).sort((a, b) => b - a);
            sortedIndices.forEach(idx => {
                onDelete(relations[idx], idx);
            });
        }
        setSelectedRelations(new Set());
        setConfirmBulkDeleteVisible(false);
    };

    const cancelBulkDelete = () => {
        setConfirmBulkDeleteVisible(false);
    };

    // Handle bulk validate
    const handleBulkValidate = () => {
        if (!onBulkValidate || selectedRelations.size === 0) return;
        const selectedIndices = Array.from(selectedRelations);
        onBulkValidate(selectedIndices);
        setSelectedRelations(new Set());
    };

    // Handle bulk unvalidate
    const handleBulkUnvalidate = () => {
        if (!onBulkUnvalidate || selectedRelations.size === 0) return;
        const selectedIndices = Array.from(selectedRelations);
        onBulkUnvalidate(selectedIndices);
        setSelectedRelations(new Set());
    };

    const allSelected = relations.length > 0 && selectedRelations.size === relations.length;
    const someSelected = selectedRelations.size > 0 && selectedRelations.size < relations.length;

    // Determine validation state of current selection
    const selectedIndicesArr = Array.from(selectedRelations);
    const selectedValidationBools = selectedIndicesArr.map(idx => !!relations[idx]?.is_validated);
    const selectionAllValidated = selectedValidationBools.length > 0 && selectedValidationBools.every(v => v);
    const selectionAllUnvalidated = selectedValidationBools.length > 0 && selectedValidationBools.every(v => !v);
    const selectionMixed = selectedValidationBools.length > 0 && !selectionAllValidated && !selectionAllUnvalidated;

    // Build consistent label list so colors match elsewhere
    const allLabels = (allCategories && allCategories.length > 0)
        ? allCategories
        : Array.from(new Set((entities || []).map(e => e.label))).sort((a, b) => a.localeCompare(b));

    return (
        <div style={{ marginTop: 32 }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
            }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: 8 }}>
                        <SparkIcon slug="link" size="medium" />
                    </span>
                    <span style={{ fontWeight: 700, fontSize: 18 }}>
                        Entity Relations ({relations.length})
                    </span>
                </div>
                {relations.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Checkbox
                            label="Select All"
                            status={allSelected ? 'active' : someSelected ? 'indeterminate' : 'inactive'}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                        {selectedRelations.size > 0 && (
                            <>
                                {onBulkValidate && (selectionMixed || selectionAllUnvalidated) && (
                                    <Button
                                        variant="primary"
                                        size="small"
                                        onClick={handleBulkValidate}
                                        disableWhenReadOnly
                                    >
                                        Validate {selectedRelations.size}
                                    </Button>
                                )}
                                {onBulkUnvalidate && (selectionMixed || selectionAllValidated) && (
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={handleBulkUnvalidate}
                                        disableWhenReadOnly
                                    >
                                        Unvalidate {selectedRelations.size}
                                    </Button>
                                )}
                                <Button
                                    variant="primary"
                                    danger
                                    size="small"
                                    startIcon={<SparkIcon slug="trash" size="small" />}
                                    onClick={handleBulkDelete}
                                    disableWhenReadOnly
                                >
                                    Delete {selectedRelations.size}
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 10 }}>
                {relations.map((rel, idx) => {
                    const source = getEntity(rel.source_start_index);
                    const target = getEntity(rel.target_start_index);
                    const isSelected = selectedRelations.has(idx);
                    const isValidated = !!rel.is_validated;

                    return (
                        <div key={idx} style={{
                            borderRadius: 12,
                            padding: '5px 18px',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 16,
                            boxShadow: 'var(--shadow-overlay)',
                            background: isSelected ? 'var(--semantic-action-primary-bg-active)' : 'transparent',
                            border: isSelected ? '1px solid var(--semantic-border-active)' : '1px solid transparent',
                        }}>
                            <Checkbox
                                label=""
                                status={isSelected ? 'active' : 'inactive'}
                                onChange={() => handleRelationSelect(idx)}
                                style={{ marginRight: 8 }}
                            />

                            {/* Source entity */}
                            <span style={{
                                background: "var(--semantic-surface-canvas)",
                                color: "var(--semantic-content-primary)",
                                borderRadius: 8,
                                padding: '4px 12px',
                                fontWeight: 600,
                                fontSize: 15,
                                marginRight: 4,
                                minWidth: 60,
                                textAlign: 'center',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 10,
                            }}>{source?.content || '—'}
                                <TagColored label={source?.label} variant={getSparkTagVariant(source?.label, allLabels)} />
                            </span>
                            {/* Relation label */}
                            <div style={{ display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: 15, margin: '0 8px' }}>
                                <div style={{ display: 'inline-block', marginRight: 4 }}>→</div>
                                <ReadOnlyGuard>
                                    <TooltipTrigger text={isValidated ? 'Validated. Click to unvalidate.' : 'Not validated. Click to validate.'}>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                padding: '0 8px',
                                                borderRadius: 6,
                                                border: isValidated ? undefined : '1.5px dashed var(--semantic-feedback-warning-on-surface)',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                if (isValidated) {
                                                    onBulkUnvalidate([idx]);
                                                } else {
                                                    onBulkValidate([idx]);
                                                }
                                            }}
                                        >
                                            {rel.label}
                                        </span>
                                    </TooltipTrigger>
                                </ReadOnlyGuard>
                                <div style={{ display: 'inline-block', marginLeft: 4 }}>→</div>
                            </div>
                            {/* Target entity */}
                            <span style={{
                                background: "var(--semantic-surface-canvas)",
                                color: "var(--semantic-content-primary)",
                                borderRadius: 8,
                                padding: '4px 12px',
                                fontWeight: 600,
                                fontSize: 15,
                                minWidth: 60,
                                textAlign: 'center',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 10,
                            }}>{target?.content || '—'}
                                <TagColored label={target?.label} variant={getSparkTagVariant(target?.label, allLabels)} />
                            </span>
                            {/* Delete icon */}
                            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                <ReadOnlyGuard>
                                    <TooltipTrigger text="Delete relation">
                                        <span style={{ color: 'var(--semantic-action-danger-content-on-surface)', cursor: 'pointer', display: 'inline-flex' }} onClick={() => onDelete(rel, idx)}>
                                            <SparkIcon slug="trash" size="small" />
                                        </span>
                                    </TooltipTrigger>
                                </ReadOnlyGuard>
                            </span>
                        </div>
                    );
                })}
            </div>
            <SparkModalPortal open={confirmBulkDeleteVisible} onClose={cancelBulkDelete}>
                <SparkModal
                    title={`Delete ${selectedRelations.size} relationship${selectedRelations.size > 1 ? 's' : ''}?`}
                    showFooter
                    danger
                    onPrimaryClick={confirmBulkDelete}
                    onTertiaryClick={cancelBulkDelete}
                    primaryLabel="Delete"
                    primaryDisableWhenReadOnly
                    tertiaryLabel="Cancel"
                    onCloseClick={cancelBulkDelete}
                >
                    <div>
                        <div>Are you sure you want to delete {selectedRelations.size} relationship{selectedRelations.size > 1 ? 's' : ''}?</div>
                        <div>This action cannot be undone.</div>
                    </div>
                </SparkModal>
            </SparkModalPortal>
        </div>
    );
}

export default RelationList; 